/**
 * SPEC-12 external guard — the read side of supervision, plus release.
 *
 *   GET  /api/orgs/:orgId/guard-events   the feed + what is frozen right now
 *   GET  /api/orgs/:orgId/guard-policy   who is allowed to release
 *   POST /api/sessions/:sessionId/unfreeze
 *
 * The feed and the containment routes 404 `feature_disabled` unless the
 * gateway's MEMBER_SUPERVISION_ENABLED is on, so the screen treats that one
 * body as "not enabled on this instance" and says so, rather than raising an
 * error boundary. guard-policy is NOT behind that flag: it answers who could
 * release even while the feature is off, which is why the release control can
 * be decided separately from whether the feed has anything in it.
 *
 * FREEZE IS NOT HERE ON PURPOSE. POST /freeze exists and has the same shape,
 * but containment arrives from an incident, not from a person browsing a list.
 * A button that stops somebody's work does not belong on a reading screen.
 *
 * NOTE ON LANGUAGE: every string in this file is English, including the
 * comments -- scripts/check-ui-english.sh scans apps/chaos-ui/src and does not
 * distinguish a comment from a label, deliberately.
 */
import { ApiError, apiJson } from './client';

/** The three levels chaos.guard_events records. */
export type GuardSeverity = 'critical' | 'warn' | 'info';

/** Loudest first — the order the filter offers them in. */
export const GUARD_SEVERITIES: readonly GuardSeverity[] = ['critical', 'warn', 'info'];

/**
 * The seventeen kinds the gateway can write. Kept as a literal list rather
 * than derived from the loaded page: a filter that only offers what happens to
 * be on screen cannot be used to look for the kind that is missing.
 */
export const GUARD_EVENT_KINDS = [
  'anomaly',
  'injection_flag',
  'injection_block',
  'topic_flag',
  'topic_block',
  'exfil_write_denied',
  'artifact_denied',
  'retrieval_capped',
  'auditor_flag',
  'supervision_denied',
  'member_action_pending',
  'freeze',
  'unfreeze',
  'suspend',
  'restore',
  'guard_context_error',
  'legacy_token_unattributed',
] as const;

export type GuardEventKind = (typeof GUARD_EVENT_KINDS)[number];

/**
 * One row of chaos.guard_events, snake_case exactly as the gateway sends it.
 * Not camelised on the way in: the screen shows `kind` and `rule_id` verbatim
 * so what a reader quotes in an incident matches what a query would find.
 */
export interface GuardEvent {
  id: string;
  user_id: string | null;
  session_id: string | null;
  card_id: string | null;
  kind: string;
  rule_id: string | null;
  severity: string;
  summary: string;
  detail: unknown;
  created_at: string;
}

/** A session with chaos.sessions.frozen_at set — stopped as of this read. */
export interface FrozenSession {
  id: string;
  card_id: string | null;
  frozen_at: string;
  frozen_by: string | null;
  freeze_reason: string | null;
}

export interface GuardEventsResponse {
  events: GuardEvent[];
  frozenSessions: FrozenSession[];
  /** Keyset cursor: feed it back as `before`. Null means the end of the feed. */
  nextBefore: string | null;
}

export interface GuardEventsQuery {
  limit?: number;
  before?: string | null;
  severity?: string | null;
  kind?: string | null;
  memberId?: string | null;
  sessionId?: string | null;
}

/**
 * The subset of GET guard-policy this screen reads. The response carries far
 * more (retention, enforcement arms, seeded defaults); typing only what is used
 * keeps an unrelated server-side addition from being a UI type error.
 */
export interface GuardPolicyResponse {
  policy: {
    freezeAuthority?: string[];
    unfreezeAuthority?: string[];
  } & Record<string, unknown>;
  decisions: {
    adminMayUnfreezeSession?: boolean;
    approverMayUnfreezeSession?: boolean;
  } & Record<string, unknown>;
  unconfirmedCount?: number;
}

function queryString(q: GuardEventsQuery): string {
  const params = new URLSearchParams();
  if (q.limit) params.set('limit', String(q.limit));
  if (q.before) params.set('before', q.before);
  if (q.severity) params.set('severity', q.severity);
  if (q.kind) params.set('kind', q.kind);
  if (q.memberId) params.set('memberId', q.memberId);
  if (q.sessionId) params.set('sessionId', q.sessionId);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const supervisionApi = {
  events(orgId: string, query: GuardEventsQuery = {}): Promise<GuardEventsResponse> {
    return apiJson<GuardEventsResponse>(`/orgs/${orgId}/guard-events${queryString(query)}`);
  },
  policy(orgId: string): Promise<GuardPolicyResponse> {
    return apiJson<GuardPolicyResponse>(`/orgs/${orgId}/guard-policy`);
  },
  /**
   * Release one session. `reason` is required by the gateway (1..2000 chars)
   * and is stored with the resulting `unfreeze` guard event, so it is the only
   * record of why containment ended.
   */
  unfreeze(
    sessionId: string,
    reason: string,
    principalFence: string,
  ): Promise<{ sessionId: string; released: boolean }> {
    return apiJson<{ sessionId: string; released: boolean }>(`/sessions/${sessionId}/unfreeze`, {
      method: 'POST',
      json: { reason },
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },
};

export const supervisionKeys = {
  all: ['supervision'] as const,
  events: (orgId: string, query: GuardEventsQuery) =>
    [...supervisionKeys.all, 'events', orgId, query] as const,
  policy: (orgId: string) => [...supervisionKeys.all, 'policy', orgId] as const,
};

/** The gateway's one body for "MEMBER_SUPERVISION_ENABLED is off". */
export function isFeatureDisabled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.code === 'feature_disabled';
}

/**
 * May THIS viewer see a release control?
 *
 * The policy answers in terms of roles, not of the person reading, so the
 * viewer's own Workspace role decides which answer applies. Every unknown
 * narrows: an unreadable policy, a missing authority list, a role we do not
 * recognise all resolve to false. Showing a button the gateway will 403 is
 * worse than not showing one — the first teaches a supervisor that release is
 * unreliable, the second teaches them who to ask.
 *
 * `org_founder` / `org_admin` / `card_approver` are the gateway's FreezeRole
 * vocabulary (guard-policy.ts), echoed verbatim in its 403 message.
 */
export function mayUnfreezeSession(
  policy: GuardPolicyResponse | null | undefined,
  orgRole: string | null | undefined,
): boolean {
  if (!policy) return false;
  const authority = Array.isArray(policy.policy?.unfreezeAuthority) &&
    policy.policy.unfreezeAuthority.every((role) => typeof role === 'string')
    ? policy.policy.unfreezeAuthority
    : null;
  if (orgRole === 'founder') {
    return authority !== null && authority.includes('org_founder');
  }
  if (orgRole === 'admin') {
    return authority !== null && authority.includes('org_admin') &&
      policy.decisions?.adminMayUnfreezeSession === true;
  }
  // Card approver is a separate gateway-resolved actor fact, not an org role.
  // This component has no verified approver fact, so every other org role and
  // every unresolved membership must narrow to false.
  return false;
}

/** Human labels for the seventeen kinds — this screen's only copy table. */
export const GUARD_KIND_LABEL: Record<string, string> = {
  anomaly: 'Anomaly',
  injection_flag: 'Injection flagged',
  injection_block: 'Injection blocked',
  topic_flag: 'Topic flagged',
  topic_block: 'Topic blocked',
  exfil_write_denied: 'Write denied (exfiltration)',
  artifact_denied: 'Artifact denied',
  retrieval_capped: 'Retrieval capped',
  auditor_flag: 'Auditor flag',
  supervision_denied: 'Supervision denied',
  member_action_pending: 'Member action pending',
  freeze: 'Session frozen',
  unfreeze: 'Session released',
  suspend: 'Member suspended',
  restore: 'Member restored',
  guard_context_error: 'Guard context error',
  legacy_token_unattributed: 'Unattributed legacy token',
};

export function guardKindLabel(kind: string): string {
  return GUARD_KIND_LABEL[kind] ?? kind;
}

/** Anything the server sends that is not one of the three reads as info. */
export function guardSeverityOf(severity: string): GuardSeverity {
  return severity === 'critical' || severity === 'warn' ? severity : 'info';
}
